/**
 * METABOLIC V5 (CLEAN ROOM)
 * COEFFICIENT REGISTRY & RESEARCH CITATIONS
 */

import { ClinicalFraction } from './types';

export const V5_CONSTANTS = {
    /**
     * GASTRIC EMPTYING (Stomach -> Gut)
     * Model: Modified Power Exponential (MPE)
     * Source: Elashoff et al., 1982 / Georgiou et al., 2024
     */
    GASTRIC: {
        rates: {
            glucose: 0.035,       // Fine-tuned for T+45m peak
            fructose: 0.035,      // Fine-tuned for T+45m peak
            sucrose: 0.03,
            lactose: 0.03,
            complex_carbs: 0.012, // Slow starch grinding
            intact_protein: 0.018, // Intermediate protein processing
            lipids: 0.010,        // Slowest lipid release
            fiber: 0.005,         // Highly persistent bulk
            free_amino_acids: 0.04,
            collagen_peps: 0.02
        } as Record<ClinicalFraction, number>,
        TERMINAL_MMC: 0.1,    // Mechanical sweep (g/min)
        LAG_BETA: 1.2,        // Solid food trituration factor
        BRAKE_INTENSITY: 50.0 // Grams of fat to trigger max duodenal brake
    },

    /**
     * ABSORPTION RATES (Gut -> Blood)
     * Model: Mari-Groop Flux
     * Source: Mari et al., 2002
     */
    ABSORPTION: {
        glucose: 0.12,        // Normalized from 0.25 to 0.12
        fructose: 0.08,
        sucrose: 0.15,
        lactose: 0.12,
        complex_carbs: 0.10,
        intact_protein: 0.08,
        free_amino_acids: 0.30,
        collagen_peps: 0.08,
        lipids: 0.015,        // Decoupled slow-path triglycerides
        fiber: 0.0
    } as Record<ClinicalFraction, number>,

    /**
     * SYSTEMIC CLEARANCE (Blood -> Cells)
     * Source: Cunningham, 1980 / Bergman, 1989
     */
    CLEARANCE: {
        glucose: 0.06,
        fructose: 0.03,
        complex_carbs: 0.03,
        intact_protein: 0.03,
        free_amino_acids: 0.06,
        collagen_peps: 0.02,
        lipids: 0.05,         // Increased to 0.05 for athlete cleanup
        insulin: 0.08         // Fast receptor binding/decay
    } as Partial<Record<ClinicalFraction | 'insulin', number>>,

    /**
     * V5.0 ADVANCED DYNAMICS
     */
    DYNAMICS: {
        INTERSTITIAL_LAG: 0.067, // 15-min plasma-to-fluid delay
        GLYCOGEN_SENSITIVITY: 3.0, // Multiplier for post-workout clearance
        INCRETIN_SENSITIVITY: 0.2, // Anticipatory insulin per gram emptied
        CIRCADIAN_AMPLITUDE: 0.2   // +/- 20% variance
    }
};
